import { ref, computed, watch, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import type { BuyRequest } from '@/js/services/RogueTypes'

export interface UseSideSelectionOptions<V extends string> {
  // Cave's Swatches use lowercase values ('dual'/'left'/'right'); Slinger X capitalizes them. Defaults to the lowercase trio.
  values?: { dual: V; left: V; right: V }
  // Cave forces both sides to qty 1 on a genuinely fresh load (no ?rid=); Slinger X has no such gate. Defaults to false.
  forceDualOnFreshLoad?: boolean
  // Slinger X's qty-reset watch also fires on mount; Cave doesn't need to since forceDualOnFreshLoad covers it. Defaults to false.
  applyOnMount?: boolean
  // Fallback when a fresh load has no qty on either side. Slinger X defaults to 'Left'; Cave doesn't need this since forceDualOnFreshLoad already forces both sides on.
  freshLoadDefault?: V
}

const DEFAULT_VALUES = { dual: 'dual', left: 'left', right: 'right' }

export function useSideSelection<V extends string = 'dual' | 'left' | 'right'>(
  buyRequest: Ref<BuyRequest>,
  opts: UseSideSelectionOptions<V> = {}
) {
  const route = useRoute()
  const values = opts.values ?? (DEFAULT_VALUES as unknown as { dual: V; left: V; right: V })

  const sideOptions = [
    { value: values.dual, label: 'Dual' },
    { value: values.left, label: 'Left' },
    { value: values.right, label: 'Right' },
  ]

  const leftSide  = computed(() => buyRequest.value.children?.[1])
  const rightSide = computed(() => buyRequest.value.children?.[2])

  const leftSideEnabled  = computed(() => (leftSide.value?.qty ?? 0) > 0)
  const rightSideEnabled = computed(() => (rightSide.value?.qty ?? 0) > 0)

  function getInitialSideSelection(): V {
    const hasLeft  = (leftSide.value?.qty ?? 0) > 0
    const hasRight = (rightSide.value?.qty ?? 0) > 0

    if (hasLeft && hasRight) return values.dual
    if (hasLeft) return values.left
    if (hasRight) return values.right

    return opts.freshLoadDefault ?? values.dual // fresh load with no qty set
  }

  // Tracks the user's last explicit side choice so we can restore it after add-to-cart resets qtys to 0.
  const lastSideSelection = ref(getInitialSideSelection()) as Ref<V>

  const sideSelection = computed<V>(() => {
    const hasLeft  = (leftSide.value?.qty ?? 0) > 0
    const hasRight = (rightSide.value?.qty ?? 0) > 0

    if (hasLeft && hasRight) return values.dual
    if (hasLeft) return values.left
    if (hasRight) return values.right

    return lastSideSelection.value // no qtys are active, fall back to what the user last picked
  })

  function setSideSelection(val: V) {
    lastSideSelection.value = val // persist the choice so sideSelection can restore it after a qty reset

    if (leftSide.value) {
      leftSide.value.qty = [values.dual, values.left].includes(val) ? 1 : 0
    }

    if (rightSide.value) {
      rightSide.value.qty = [values.dual, values.right].includes(val) ? 1 : 0
    }
  }

  // A restored recipe (route.query.rid present) is left exactly as loaded.
  if (opts.forceDualOnFreshLoad && !route.query.rid) {
    if (leftSide.value) leftSide.value.qty = 1
    if (rightSide.value) rightSide.value.qty = 1
  }

  // After add-to-cart resets both child qtys to 0, re-apply the user's last selection.
  watch(
    () => [leftSide.value?.qty, rightSide.value?.qty],
    ([leftQty, rightQty]) => {
      if (leftQty === 0 && rightQty === 0) {
        setSideSelection(sideSelection.value)
      }
    },
    { immediate: opts.applyOnMount ?? false }
  )

  return {
    sideOptions,
    leftSide,
    rightSide,
    leftSideEnabled,
    rightSideEnabled,
    sideSelection,
    setSideSelection,
  }
}
